import * as React from "react";
import {
  Settings,
  BookOpen,
  Box,
  Plug,
  MessageSquare,
  Variable,
  MoreVertical,
  FileText,
} from "lucide-react";
import { NavigationTab } from "../../types/navigationTypes";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ProjectSelector } from "./ProjectSelector";
import { configService } from "../services/configService";

interface AppHeaderProps {
  // Navigation props
  activeTab: NavigationTab;
  onTabChange: (tab: NavigationTab) => void;
  onOpenSettings: () => void;
  onSelectExecution?: (executionId: string, workpillId: string, executionName?: string) => void;
  
  // Project state
  hasSelectedProject?: boolean;
  selectedExecutionId?: string;
  isProjectSelectorOpen?: boolean;
  onProjectSelectorOpenChange?: (isOpen: boolean) => void;
  
  // Agent mode
  isAgentMode?: boolean;
  onToggleAgentMode?: () => void;
}

const AppHeader: React.FC<AppHeaderProps> = ({ 
  activeTab,
  onTabChange,
  onOpenSettings,
  onSelectExecution,
  hasSelectedProject = false,
  selectedExecutionId,
  isProjectSelectorOpen,
  onProjectSelectorOpenChange,
  isAgentMode = false,
  onToggleAgentMode
}) => {
  const handleAgentClick = () => {
    if (isAgentMode) {
      // Si el agente está activo, desactivarlo
      onToggleAgentMode?.();
    } else {
      // Si el agente está inactivo, activarlo y navegar al chat
      onToggleAgentMode?.();
      if (activeTab !== 'chat') {
        onTabChange('chat');
      }
    }
  };

  const handleBlocksClick = () => {
    if (activeTab === 'blocks') {
      onTabChange('chat'); // Toggle back to chat
    } else {
      onTabChange('blocks');
    }
  };

  const handleKBClick = () => {
    if (activeTab === 'knowledgebase') {
      onTabChange('chat'); // Toggle back to chat
    } else {
      onTabChange('knowledgebase');
    }
  };

  const handleDataClick = () => {
    if (activeTab === 'data') {
      onTabChange('chat'); // Toggle back to chat
    } else {
      onTabChange('data');
    }
  };

  const handleIntegrationsClick = () => {
    if (activeTab === 'integrations') {
      onTabChange('chat'); // Toggle back to chat
    } else {
      onTabChange('integrations');
    }
  };

  const handleTemplatesClick = () => {
    if (activeTab === 'templates') {
      onTabChange('chat');
    } else {
      onTabChange('templates');
    }
  };

  const iconNavClass =
    "h-9 w-9 shrink-0 rounded-lg transition-colors duration-200 active:scale-100 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";
  const iconStroke = { strokeWidth: 2 as const };

  return (
    <header className="flex h-14 shrink-0 items-center justify-between border-b border-slate-100 bg-white px-4 font-sans shadow-sm relative z-50">
      {/* Left Section: Logo & Project Selector */}
      <div className="flex items-center gap-4">
        <img 
          src="assets/favicon-galileo-verde.png" 
          alt="WorkOps Logo"
          className="h-5"
        />
        
        <div className="h-6 w-px bg-slate-200 mx-1" />

        <ProjectSelector 
          onSelectExecution={onSelectExecution} 
          selectedExecutionId={selectedExecutionId}
          isOpen={isProjectSelectorOpen}
          onOpenChange={onProjectSelectorOpenChange}
        />
      </div>

      {/* Right Section: Actions & Settings */}
      <div className="flex items-center gap-1">
        
        {/* Navigation: Agent, KB, templates — blocks & variables live under the more menu */}
        {hasSelectedProject && (
          <>
            {/* Agent Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleAgentClick}
              className={cn(
                iconNavClass,
                isAgentMode
                  ? "bg-slate-100 text-slate-800 hover:bg-slate-100"
                  : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
              )}
              title="Agent Mode"
            >
              <MessageSquare size={18} {...iconStroke} className="shrink-0" />
            </Button>

            {/* KB Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleKBClick}
              className={cn(
                iconNavClass,
                activeTab === 'knowledgebase'
                  ? "bg-slate-100 text-slate-800 hover:bg-slate-100"
                  : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
              )}
              title="Knowledge Base"
            >
              <BookOpen size={18} {...iconStroke} className="shrink-0" />
            </Button>

            {/* Execution templates */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleTemplatesClick}
              className={cn(
                iconNavClass,
                activeTab === 'templates'
                  ? "bg-slate-100 text-slate-800 hover:bg-slate-100"
                  : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
              )}
              title="Execution templates"
            >
              <FileText size={18} {...iconStroke} className="shrink-0" />
            </Button>

            <div className="mx-0.5 h-4 w-px shrink-0 bg-slate-200" />
          </>
        )}

        {/* Menu Dropdown: Integraciones y Configuración */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={cn(iconNavClass, "text-slate-500 hover:text-slate-700 hover:bg-slate-50")}
              title="More options"
            >
              <MoreVertical size={18} {...iconStroke} className="shrink-0" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            {hasSelectedProject && (
              <>
                <DropdownMenuItem onClick={handleBlocksClick} className="cursor-pointer">
                  <Box size={16} {...iconStroke} className="mr-2 shrink-0" />
                  <span>Document blocks</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleDataClick} className="cursor-pointer">
                  <Variable size={16} {...iconStroke} className="mr-2 shrink-0" />
                  <span>Project variables</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            {hasSelectedProject && !configService.isB2C() && (
              <DropdownMenuItem onClick={handleIntegrationsClick} className="cursor-pointer">
                <Plug size={16} {...iconStroke} className="mr-2 shrink-0" />
                <span>Integrations</span>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={onOpenSettings} className="cursor-pointer">
              <Settings size={16} {...iconStroke} className="mr-2 shrink-0" />
              <span>Settings</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export default AppHeader;

